import { inject, Injectable } from '@angular/core';

import { SynopticNodeData } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import {
    RestoreActionEnum,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { EditEventAction, EntityProperties } from '@connect-services/client/models/edit-event.model';
import {
    EditAction,
    ExtraObject,
    Layer,
    LayerControllerGetRequestParams,
    SynopticBoxPlanCableBySide,
} from '@connect-services/client/sdk/api';
import { EditModeStateService, EditState } from '@connect-services/client/services/edit-mode-state.service';
import { ExtraObjectService } from '@connect-services/client/services/edition/extra-object.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';

type BoxPlanEditModeStateData = {
    cables: SynopticBoxPlanCableBySide;
    cassettes: SynopticPlanItem[][];
    drawerId?: string | null;
};
type UpdateEditModeStateParameters = {
    cables: SynopticBoxPlanCableBySide;
    cassettes: SynopticPlanItem[][];
    drawerId?: string | null;
    initialCables: SynopticBoxPlanCableBySide;
    initialCassettes: SynopticPlanItem[][];
    layerId: string;
    node: SynopticNodeData;
    projectId: string;
};

@Injectable({
    providedIn: 'root',
})
export class BoxPlanEditModeStateService extends EditModeStateService {
    private readonly extraObjectService = inject(ExtraObjectService);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly mainEditModeStateService = inject(EditModeStateService);

    public getEventCanceledOrRestored(action: RestoreActionEnum, projectId?: string): EntityProperties | undefined {
        if (!projectId) {
            return;
        }

        if (action === RestoreActionEnum.CANCEL) {
            const event = this.cancelLastEdit(projectId);

            return event?.actions[0].before;
        }

        const event = this.reapplyLastCanceledEdit(projectId);

        return event?.actions[0].after;
    }

    public async saveFiberingUpdatesIntoProjectState(
        projectId: string,
        depositId: string,
        extraObjects: ExtraObject[],
    ): Promise<void> {
        const projectState = this.mainEditModeStateService.getState(projectId);
        const layers = await this.getLayers(projectId);

        const extraObjectsLayers = this.extraObjectService.getExtraObjectsLayers(extraObjects, layers, depositId);
        const edits = this.extraObjectService.getExtraObjectsEditActions(
            extraObjects,
            extraObjectsLayers,
            projectState,
            depositId,
        );

        const enabledEdits = edits.filter((editEvent: EditEventAction) => editEvent.entityId);
        this.mainEditModeStateService.addEdit(projectId, { actions: enabledEdits });
        this.clearEditModeState(projectId);
    }

    public updateEditModeState(params: UpdateEditModeStateParameters): void {
        const entityId = params.drawerId ? `${params.node.uuid}-${params.drawerId}` : params.node.uuid;
        const previousElement = this.getEntityState(params.projectId, params.layerId, entityId);
        const isInitialData =
            JSON.stringify(params.cassettes) === JSON.stringify(params.initialCassettes) &&
            JSON.stringify(params.cables) === JSON.stringify(params.initialCables) &&
            !previousElement;
        const dataHasChanged =
            JSON.stringify(params.cassettes) !== JSON.stringify(previousElement?.properties?.['cassettes']) ||
            JSON.stringify(params.cables) !== JSON.stringify(previousElement?.properties?.['cables']);

        if (isInitialData || !dataHasChanged) {
            return;
        }

        const beforeData = (previousElement?.properties as BoxPlanEditModeStateData) ?? {
            cables: params.initialCables,
            cassettes: params.initialCassettes,
        };
        const action = this.getAction(beforeData, {
            cables: params.cables,
            cassettes: params.cassettes,
            drawerId: params.drawerId,
        });

        this.addEdit(params.projectId, {
            actions: [
                {
                    action: action,
                    after: { cables: params.cables, cassettes: params.cassettes, drawerId: params.drawerId },
                    before: previousElement?.properties ?? {
                        cables: params.initialCables,
                        cassettes: params.initialCassettes,
                        drawerId: params.drawerId,
                    },
                    businessIdentifier: { key: 'id', value: params.node.id },
                    entityId: entityId,
                    layerId: params.layerId,
                    tableName: '',
                },
            ],
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override clearEditSessionLogState(): Promise<void> {
        return Promise.resolve();
    }

    protected override getEditLocalStorageKey(projectId: string, action: 'events' | 'state'): string {
        return `boxPlanEditMode/${projectId}/${action}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override updateEditSessionLogState(projectId: string, state: EditState): Promise<void> {
        return Promise.resolve();
    }

    private getAction(previousData: BoxPlanEditModeStateData, currentData: BoxPlanEditModeStateData): EditAction {
        if (
            previousData.cassettes.length > currentData.cassettes.length ||
            Object.values(previousData.cables).flat().length > Object.values(currentData.cables).flat().length
        ) {
            return EditAction.DELETE;
        }

        if (
            previousData.cassettes.length < currentData.cassettes.length ||
            Object.values(previousData.cables).flat().length < Object.values(currentData.cables).flat().length
        ) {
            return EditAction.CREATE;
        }

        return EditAction.UPDATE;
    }

    private getLayers(projectId: string): Promise<Layer[]> {
        return this.fetchAllService.getAll<LayerControllerGetRequestParams, Layer>(
            this.layersService.layerControllerGet.bind(this.layersService),
            {
                geomFilter: 'ALL',
                project: projectId,
            },
        );
    }
}
